import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AvatarLatencyClientMarks, AvatarLatencyClientReport, AvatarLatencyInputMode, AvatarLatencyProvider } from '@shared/index';
import { firstValueFrom, Subscription } from 'rxjs';
import { SocketService } from '../socket.service';
import { AvatarAudioProbe } from './avatar-audio-probe';

export type VoiceInputTiming = {
    speechEndAt: number | null;
    submittedAt: number;
    sttDoneAt: number;
};

type ClientTurn = {
    turnId: string;
    inputMode: AvatarLatencyInputMode;
    t0: number;
    marks: AvatarLatencyClientMarks;
};

const TURN_TIMEOUT_MS = 60_000;
// Provider-Event und Pegel kommen in beliebiger Reihenfolge – kurz auf das jeweils andere warten
const AFTER_AUDIBLE_GRACE_MS = 1_500;
const AFTER_PROVIDER_GRACE_MS = 8_000;

declare global {
    interface Window { __ragaiLatencyReports?: AvatarLatencyClientReport[] }
}

/** Misst pro Avatar-Gesprächsrunde die Zeit von der Eingabe bis zum hörbaren Audio (Client-Uhr). */
@Injectable({ providedIn: 'root' })
export class TurnLatencyService {
    private http = inject(HttpClient);
    private socketService = inject(SocketService);
    private provider: AvatarLatencyProvider | null = null;
    private botId = '';
    private isBenchmark = false;
    private turn: ClientTurn | null = null;
    private subscriptions: Subscription[] = [];
    private probe = new AvatarAudioProbe();
    private timeoutId: number | null = null;
    private graceId: number | null = null;

    public enable(provider: AvatarLatencyProvider, botId: string): void {
        this.disable();
        this.provider = provider;
        this.botId = botId;
        this.isBenchmark = new URLSearchParams(window.location.search).get('latencybenchmark') === '1';
        this.subscriptions.push(
            this.socketService.onRunUpdate.subscribe(() => { this.mark('firstRunUpdate'); }),
            this.socketService.onRunComplete.subscribe((item) => {
                if (item.role !== 'assistant' || !this.turn?.marks.chatSent) return;
                this.mark('runComplete');
                const turn = this.turn;
                this.probe.arm((at) => { this.onAudible(turn, at); });
            }),
            this.socketService.onDidSpeak.subscribe(() => { this.mark('didSpeakReceived'); }),
            this.socketService.onStateUpdate.subscribe((data) => {
                if (this.provider === 'liveavatar' && data.sessionState === 'speaking') this.markProviderSpeakStart();
            })
        );
    }

    public disable(): void {
        if (this.turn) this.finish(this.turn);
        this.subscriptions.forEach(s => { s.unsubscribe(); });
        this.subscriptions = [];
        this.probe.destroy();
        this.probe = new AvatarAudioProbe();
        this.provider = null;
    }

    public attachAudio(stream: MediaStream): void {
        this.probe.attach(stream);
    }

    public startTextTurn(): void {
        this.begin('text', performance.now(), {});
    }

    public startVoiceTurn(timing: VoiceInputTiming): void {
        const marks: AvatarLatencyClientMarks = { sttDone: Math.round(timing.sttDoneAt - timing.submittedAt) };
        if (timing.speechEndAt !== null) marks.silenceWait = Math.round(timing.submittedAt - timing.speechEndAt);
        this.begin('voice', timing.submittedAt, marks);
    }

    /**
     * Markiert den Versand des laufenden Turns.
     * @returns Die turnId genau einmal für den nächsten Chat-Request, sonst undefined.
     */
    public consumeTurnIdForChat(): string | undefined {
        if (!this.turn || this.turn.marks.chatSent !== undefined) return undefined;
        this.mark('chatSent');
        this.timeoutId = window.setTimeout(() => { if (this.turn) this.finish(this.turn); }, TURN_TIMEOUT_MS);
        return this.turn.turnId;
    }

    public markProviderSpeakStart(): void {
        const turn = this.turn;
        if (!turn?.marks.runComplete || turn.marks.providerSpeakStart !== undefined) return;
        this.mark('providerSpeakStart');
        this.scheduleFinish(turn, turn.marks.audioAudible !== undefined ? AFTER_AUDIBLE_GRACE_MS : AFTER_PROVIDER_GRACE_MS);
    }

    private begin(inputMode: AvatarLatencyInputMode, t0: number, marks: AvatarLatencyClientMarks): void {
        if (!this.provider) return;
        if (this.turn) this.finish(this.turn);
        this.turn = { turnId: crypto.randomUUID(), inputMode, t0, marks };
    }

    private mark(key: keyof AvatarLatencyClientMarks, at: number = performance.now()): void {
        if (!this.turn?.marks.chatSent && key !== 'chatSent') return;
        if (!this.turn || this.turn.marks[key] !== undefined) return;
        this.turn.marks[key] = Math.round(at - this.turn.t0);
    }

    private onAudible(turn: ClientTurn, at: number): void {
        if (this.turn !== turn) return;
        this.mark('audioAudible', at);
        this.scheduleFinish(turn, turn.marks.providerSpeakStart !== undefined ? 0 : AFTER_AUDIBLE_GRACE_MS);
    }

    private scheduleFinish(turn: ClientTurn, delay: number): void {
        if (this.graceId !== null) clearTimeout(this.graceId);
        this.graceId = window.setTimeout(() => { if (this.turn === turn) this.finish(turn); }, delay);
    }

    private finish(turn: ClientTurn): void {
        if (this.turn === turn) this.turn = null;
        if (this.timeoutId !== null) { clearTimeout(this.timeoutId); this.timeoutId = null; }
        if (this.graceId !== null) { clearTimeout(this.graceId); this.graceId = null; }
        this.probe.disarm();
        if (turn.marks.chatSent === undefined || !this.provider) return;

        const report: AvatarLatencyClientReport = {
            turnId: turn.turnId,
            provider: this.provider,
            inputMode: turn.inputMode,
            source: this.isBenchmark ? 'benchmark' : 'prod',
            interrupted: turn.marks.audioAudible === undefined && turn.marks.providerSpeakStart === undefined,
            client: turn.marks
        };
        if (this.isBenchmark) (window.__ragaiLatencyReports ??= []).push(report);
        void firstValueFrom(this.http.post(`/api/user/chat/${this.botId}/latency`, report)).catch(() => { /* Messung darf den Bot nie stören */ });
    }
}
